import { useState, useEffect } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarClock, Loader2, Search, CheckCircle2, X, UserCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { modalV2OverlayClass } from '../utils/modalUiV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_CALL_PURPOSE = 'Price Enquiry';

/**
 * Bulk "Schedule Calls" modal — takes a list of already-selected customers
 * (from any customer list/folder in the Sales Hub) and schedules ONE call per
 * customer in Zoho, all with the same assignee, date/time and purpose.
 */
export const BulkScheduleCallModal = ({ isOpen, onClose, selectedItems = [], onScheduled = null }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const _tomorrow9am = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [scheduleFor, setScheduleFor] = useState(_tomorrow9am());
  const [purposes, setPurposes] = useState([]);
  const [purpose, setPurpose] = useState(DEFAULT_CALL_PURPOSE);
  const [description, setDescription] = useState('');

  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [assigneeResults, setAssigneeResults] = useState([]);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // { success_count, fail_count, results: [] }

  useEffect(() => {
    if (!isOpen) return;
    setScheduleFor(_tomorrow9am());
    setPurpose(DEFAULT_CALL_PURPOSE);
    setDescription('');
    setAssigneeQuery('');
    setAssigneeResults([]);
    setSelectedAssignee(null);
    setResults(null);
    api.get(`${API}/zoho/call-purposes`)
      .then(r => setPurposes(r.data.purposes || []))
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const q = assigneeQuery;
      if (!q || q.trim().length < 2) { setAssigneeResults([]); return; }
      setAssigneeLoading(true);
      api.get(`${API}/users/search-light`, { params: { q } })
        .then(r => setAssigneeResults(r.data.users || []))
        .catch(() => setAssigneeResults([]))
        .finally(() => setAssigneeLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [assigneeQuery, isOpen]);

  const pickAssignee = (u) => {
    setSelectedAssignee(u);
    setAssigneeQuery(u.display_name || u.email);
    setAssigneeResults([]);
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) return toast.error('No customers selected');
    if (!selectedAssignee?.email) return toast.error('Pick who to assign these calls to');
    if (!scheduleFor) return toast.error('Pick a date');

    setSubmitting(true);
    try {
      const res = await api.post(`${API}/zoho/schedule-calls-bulk`, {
        items: selectedItems.map(i => ({ account_id: i.account_id, account_name: i.account_name, contact_id: i.contact_id || null })),
        call_start_time: scheduleFor,
        assignee_email: selectedAssignee.email,
        call_purpose: purpose,
        description: description || null,
      });
      setResults(res.data);
      const { success_count, fail_count } = res.data;
      if (fail_count === 0) {
        toast.success(`Scheduled ${success_count} call${success_count !== 1 ? 's' : ''} for ${selectedAssignee.display_name || selectedAssignee.email}`);
        onScheduled?.();
      } else {
        toast.warning(`${success_count} scheduled, ${fail_count} failed — see details below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to schedule calls');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const fieldCardClass = "rounded-xl border border-gray-200 bg-white shadow-sm p-3 transition-shadow hover:shadow-md";
  const inputClass = "rounded-lg border-2 border-gray-300 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 h-10";

  return (
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center p-3 ${modalV2OverlayClass(modalUiV2)}`}
      style={modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 10010 }}
      data-testid="bulk-schedule-call-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-y-auto border-2 border-orange-300 flex flex-col" style={{ maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '92vh' }}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-orange-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ee7203 0%, #f59e42 100%)' }}>
              <CalendarClock className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-extrabold text-gray-900 tracking-tight leading-tight truncate">Schedule Calls</h2>
              <p className="text-[11px] text-gray-500 truncate">{selectedItems.length} customer{selectedItems.length !== 1 ? 's' : ''} selected</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100 flex-shrink-0" data-testid="bulk-schedule-modal-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Selected customers preview */}
          <div className={fieldCardClass}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Customers ({selectedItems.length})</Label>
            <div className="mt-1.5 max-h-24 overflow-y-auto flex flex-wrap gap-1.5">
              {selectedItems.map(i => (
                <span key={i.account_id} className="text-[11px] font-semibold bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-2 py-0.5">
                  {i.account_name || i.account_id}
                </span>
              ))}
            </div>
          </div>

          {/* Date */}
          <div className={fieldCardClass}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Date &amp; time *</Label>
            <Input
              type="datetime-local"
              value={scheduleFor}
              onChange={(e) => setScheduleFor(e.target.value)}
              className={`mt-1.5 ${inputClass}`}
              data-testid="bulk-schedule-datetime-input"
            />
          </div>

          {/* Purpose */}
          <div className={fieldCardClass}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Call Purpose</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger className={`mt-1.5 ${inputClass}`} data-testid="bulk-schedule-purpose-select">
                <SelectValue placeholder="Choose a purpose" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72 z-[10020] rounded-xl border-gray-200 shadow-xl">
                {purposes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Assignee */}
          <div className={fieldCardClass}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1">
              <UserCheck className="w-3.5 h-3.5" /> Assign to *
            </Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                value={assigneeQuery}
                onChange={(e) => { setAssigneeQuery(e.target.value); if (selectedAssignee) setSelectedAssignee(null); }}
                placeholder="Search by name or email"
                className={`flex-1 ${inputClass}`}
                data-testid="bulk-schedule-assignee-search"
                autoComplete="off"
              />
              <Button type="button" disabled={assigneeLoading} className="rounded-xl h-10 w-10 p-0 bg-orange-600 hover:bg-orange-700">
                {assigneeLoading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Search className="w-4 h-4 text-white" />}
              </Button>
            </div>
            {!selectedAssignee && (assigneeLoading || assigneeResults.length > 0) && (
              <div className="border border-gray-200 rounded-xl divide-y max-h-48 overflow-y-auto mt-2 bg-white shadow-inner">
                {assigneeLoading ? (
                  <div className="p-3 text-center text-gray-500 text-sm">Searching…</div>
                ) : assigneeResults.length === 0 ? (
                  <div className="p-3 text-center text-gray-500 text-sm">No users found</div>
                ) : (
                  assigneeResults.map((u) => (
                    <button key={u.email} type="button" onClick={() => pickAssignee(u)}
                      className="w-full text-left p-3 hover:bg-orange-50 transition-colors"
                      data-testid={`bulk-schedule-assignee-option-${u.email}`}>
                      <div className="font-semibold text-sm text-gray-900">{u.display_name || u.email}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </button>
                  ))
                )}
              </div>
            )}
            {selectedAssignee && (
              <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-semibold flex-1">{selectedAssignee.display_name || selectedAssignee.email}</span>
                <button type="button" onClick={() => { setSelectedAssignee(null); setAssigneeQuery(''); }}
                  className="text-[11px] text-gray-400 hover:text-gray-600 underline">
                  change
                </button>
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Note to user (optional)</Label>
              <textarea
                className="w-full rounded-lg border-2 border-gray-300 bg-white focus:border-orange-400 px-3 py-2 text-sm resize-y min-h-[48px] focus:outline-none focus:ring-2 focus:ring-orange-200 mt-1"
                placeholder="Applies to every call in this batch…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="bulk-schedule-notes-input"
              />
            </div>
          </div>

          {/* Per-item results */}
          {results && (
            <div className={fieldCardClass} data-testid="bulk-schedule-results">
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                Results — {results.success_count} succeeded, {results.fail_count} failed
              </Label>
              <div className="mt-1.5 max-h-40 overflow-y-auto space-y-1">
                {results.results.map((r) => (
                  <div key={r.account_id} className={`flex items-center gap-2 text-xs px-2 py-1 rounded-lg ${r.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                    {r.success ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                    <span className="font-semibold truncate">{r.account_name || r.account_id}</span>
                    {!r.success && <span className="text-[11px] truncate">{r.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-2 sticky bottom-0 bg-white -mx-4 px-4 py-3 border-t border-orange-100 mt-2">
            <Button variant="outline" onClick={onClose} className="rounded-lg h-10 px-4 border-2" data-testid="bulk-schedule-cancel-btn">
              {results ? 'Close' : 'Cancel'}
            </Button>
            {!results && (
              <Button
                onClick={handleSubmit}
                disabled={submitting || selectedItems.length === 0 || !selectedAssignee}
                className="rounded-lg h-10 px-5 text-white font-semibold shadow-md hover:shadow-lg transition-all"
                style={{ background: 'linear-gradient(135deg, #ee7203 0%, #f59e42 100%)' }}
                data-testid="bulk-schedule-submit-btn"
              >
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Scheduling…</>) : (<><CalendarClock className="w-4 h-4 mr-2" />Schedule {selectedItems.length} Call{selectedItems.length !== 1 ? 's' : ''}</>)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkScheduleCallModal;
